import type { Component } from '@bugbug/core/types/components';
import type { Test } from '@bugbug/core/types/tests';

import { baseApi } from '../baseApi/baseApi';

import { COMPONENTS_API_TAGS } from './components.constants';

const { COMPONENT_TAG, COMPONENTS_LIST_TAG } = COMPONENTS_API_TAGS;

export const componentsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // POST /components/<id>/clone
    cloneComponent: builder.mutation<Component, Component['id']>({
      query: (id) => ({
        url: `/components/${id}/clone/`,
        method: 'POST',
      }),

      invalidatesTags: [COMPONENT_TAG],
    }),

    // DELETE /components/<id>/
    deleteComponent: builder.mutation<Component, Component['id']>({
      query: (id) => ({
        url: `/components/${id}/`,
        method: 'DELETE',
      }),

      invalidatesTags: [COMPONENT_TAG],
    }),

    // GET /components/<id>/
    getComponent: builder.query<Component, Component['id']>({
      query: (id) => ({
        url: `/components/${id}/`,
        method: 'GET',
      }),

      providesTags: (result) =>
        result
          ? [{ type: COMPONENT_TAG, id: result.id }, COMPONENT_TAG]
          : [{ type: COMPONENT_TAG, id: 'ELEMENT' }],
    }),

    // GET /components/
    getComponents: builder.query<Component[], string>({
      query: (projectId) => ({
        url: '/components/',
        method: 'GET',
        params: { projectId },
      }),

      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: COMPONENT_TAG, id })), COMPONENT_TAG]
          : [COMPONENTS_LIST_TAG],
    }),

    // GET /components/<id>/related-tests/
    getRelatedTests: builder.query<Test[], Component['id']>({
      query: (id) => ({
        url: `/components/${id}/related-tests/`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: COMPONENT_TAG, id })), COMPONENT_TAG] : [],
    }),

    // POST /components/<id>/rename/
    renameComponent: builder.mutation<Component, Pick<Component, 'id' | 'name'>>({
      query: ({ id, name }) => ({
        url: `/components/${id}/rename/`,
        method: 'POST',
        data: { name },
      }),
      invalidatesTags: [COMPONENT_TAG],
    }),
  }),
});

export const {
  useCloneComponentMutation,
  useDeleteComponentMutation,
  useGetComponentQuery,
  useGetComponentsQuery,
  useGetRelatedTestsQuery,
  useRenameComponentMutation,
} = componentsApi;

// Helpers
export const invalidateComponentsList = (ids?: Component['id'][]) => {
  if (!ids) return componentsApi.util.invalidateTags([COMPONENT_TAG]);
  return componentsApi.util.invalidateTags(ids.map((id) => ({ type: COMPONENT_TAG, id })));
};
